import { devDebug } from "../lib/devLog";
import { useCallback, useEffect, useRef, useState } from "react";
import { listConversations } from "../lib/dmApi";
import { isConversationMuted } from "../lib/dmMute";
import { playDmPing } from "../lib/dmSoundPref";
import { loadIdentity } from "../lib/identity";

// Polls the DM conversation list every ~15s and returns the number of
// UNIQUE conversations that currently show at least one unread message.
// Also fires a browser Notification when a NEW unread arrives (compared
// to the last poll) so users get pinged even while browsing another tab.

export const OPEN_DM_NOTIFICATION_EVENT = "opinione-libera:open-dm";

function tryNotify(title, body, peerId, conversationId) {
  if (typeof window === "undefined" || !("Notification" in window)) return;
  if (Notification.permission !== "granted") return;
  try {
    const n = new Notification(title, {
      body,
      tag: `opinione-libera-dm-${conversationId || peerId || "new"}`,
      silent: false,
    });
    n.onclick = () => {
      try {
        window.focus();
        window.dispatchEvent(new CustomEvent(OPEN_DM_NOTIFICATION_EVENT, { detail: { peerId } }));
        n.close();
      } catch (err) {
        devDebug("Notification click failed", err?.message);
      }
    };
    // Give the ping a lifetime — most browsers auto-close after a few
    // seconds; this is a defensive fallback.
    setTimeout(() => { try { n.close(); } catch (err) { devDebug("Notification.close failed", err?.message); } }, 6000);
  } catch (err) {
    devDebug("Notification failed:", err);
  }
}

export function useUnreadDmCount(pollMs = 15000) {
  const [count, setCount] = useState(0);
  const [ready, setReady] = useState(false);
  const lastPerConvRef = useRef(new Map());
  const askedPermissionRef = useRef(false);

  const refresh = useCallback(async () => {
    const id = loadIdentity();
    if (!id) {
      setCount(0);
      return;
    }
    if (
      typeof window !== "undefined" &&
      "Notification" in window &&
      Notification.permission === "default" &&
      !askedPermissionRef.current
    ) {
      askedPermissionRef.current = true;
      try {
        void Notification.requestPermission();
      } catch (err) {
        // Older browsers (Safari <16) throw sync errors on this API call.
        devDebug("Notification.requestPermission unsupported", err?.message);
      }
    }
    try {
      const items = await listConversations();
      const prev = lastPerConvRef.current;
      const nextMap = new Map();
      let c = 0;
      for (const conv of items) {
        const u = conv.unreadCount || 0;
        nextMap.set(conv.id, u);
        if (u > 0) c += 1;
        const before = prev.get(conv.id);
        // Fire a browser notification ONLY when unread count increased
        // since our last snapshot AND the conversation is not muted.
        if (before !== undefined && u > before && !isConversationMuted(conv.id)) {
          tryNotify(
            `Nuovo messaggio da ${conv.peer?.displayName || "un utente"}`,
            "Apri You Voice per leggere il messaggio cifrato.",
            conv.peer?.identityId,
            conv.id,
          );
          // Best-effort audio ping (opt-out, tab-must-be-hidden — see
          // dmSoundPref.js). Notification-only users still hear the OS
          // sound; this covers browsers where site Notifications are silent.
          playDmPing();
        }
      }
      lastPerConvRef.current = nextMap;
      setCount(c);
    } catch (err) {
      devDebug("useUnreadDmCount: fetch failed", err);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, pollMs);
    return () => clearInterval(t);
  }, [refresh, pollMs]);

  return { count, ready, refresh };
}
